import type {
  AndExpression,
  CompareExpression,
  CompareOperator,
  NotExpression,
  OrExpression,
  RuleExpression,
} from '@tsagko/label-contracts'

export type TopOp = 'and' | 'or'

export function emptyCompare(): CompareExpression {
  return { op: 'compare', field: '', operator: '=', value: '' }
}

export function emptyGroup(op: TopOp = 'or'): AndExpression | OrExpression {
  return { op, operands: [emptyCompare()] }
}

export function normaliseToTopGroup(expr: RuleExpression): AndExpression | OrExpression {
  if (expr.op === 'and' || expr.op === 'or') return expr
  return { op: 'and', operands: [expr] }
}

export function isSimpleCondition(node: RuleExpression): node is CompareExpression {
  return node.op === 'compare'
}

export function isGroup(node: RuleExpression): node is AndExpression | OrExpression {
  return node.op === 'and' || node.op === 'or'
}

export function isNot(node: RuleExpression): node is NotExpression {
  return node.op === 'not'
}

export function removeOperand(
  group: AndExpression | OrExpression,
  index: number
): AndExpression | OrExpression {
  const next = [...group.operands]
  next.splice(index, 1)
  if (next.length === 0) next.push(emptyCompare())
  return { ...group, operands: next }
}

export function replaceOperand(
  group: AndExpression | OrExpression,
  index: number,
  replacement: RuleExpression
): AndExpression | OrExpression {
  const next = [...group.operands]
  next[index] = replacement
  return { ...group, operands: next }
}

export function appendOperand(
  group: AndExpression | OrExpression,
  operand: RuleExpression
): AndExpression | OrExpression {
  return { ...group, operands: [...group.operands, operand] }
}

export function setTopOp(
  group: AndExpression | OrExpression,
  op: TopOp
): AndExpression | OrExpression {
  return { ...group, op }
}

export function describeOperator(op: CompareOperator): string {
  switch (op) {
    case '=':
      return 'equals'
    case '!=':
      return 'does not equal'
    case '<':
      return 'is less than'
    case '<=':
      return 'is less than or equal to'
    case '>':
      return 'is greater than'
    case '>=':
      return 'is greater than or equal to'
    case 'contains':
      return 'contains'
    case 'startsWith':
      return 'starts with'
    case 'endsWith':
      return 'ends with'
    case 'in':
      return 'is one of'
    case 'exists':
      return 'exists'
    case 'matches':
      return 'matches regex'
  }
}

export function describeRule(expr: RuleExpression, depth = 0): string {
  if (isSimpleCondition(expr)) {
    const valueText =
      expr.operator === 'exists'
        ? ''
        : ` ${JSON.stringify(expr.value)}`
    return `${expr.field || '<field>'} ${describeOperator(expr.operator)}${valueText}`
  }
  if (isNot(expr)) {
    return `NOT (${describeRule(expr.operand, depth + 1)})`
  }
  if (isGroup(expr)) {
    const joiner = expr.op === 'and' ? ' AND ' : ' OR '
    if (expr.operands.length === 0) return '(empty)'
    if (expr.operands.length === 1) return describeRule(expr.operands[0]!, depth + 1)
    const parts = expr.operands.map(o => {
      const inner = describeRule(o, depth + 1)
      return isGroup(o) && depth < 2 ? `(${inner})` : inner
    })
    return parts.join(joiner)
  }
  return '(unknown expression)'
}
